import { isEqual } from 'lodash'

import { calculateAge } from '../models/interface.helper'
import {
  getHorseRequiredFields,
  getUserRequiredFields,
  hasInvalidRequiredField,
} from './requiredFields'
import { isPaymentPending, isRefundPending } from './payment'
import { haveTeamMember } from './teamMembers'

import { IRiderTeamMemberInterface } from '../models/rider-team-member/riderTeamMember.interface'
import {
  IRegistrationByDayInterface,
  IHorseSelected,
} from '../models/registrations-by-day/registrationByDay.interface'
import { TEventRegisteredUsers } from '../models/event-registered-users/event-registered-users.interface'
import { IEventTeamMemberInterface } from '../hooks/useEventRiderTeamMembers'
import { IRegisteredUser, IRider } from '../types/entryRider'
import { IEventReviewPublish } from '../models/event-review-publish/event-review-publish.interface'
import { IUserInterface } from '../models/users/user.interface'
import { IRegistrationFeesInterface } from '../models/registration-fees/registrationFees.interface'
import { IUserDocument } from '../models/user-documents/user-documents.interface'
import { IHorseData } from '../models/horse/horse.interface'
import { IHorseCompetitorDocument } from '../models/horse-competiton-paperwork/horse-competiton-paperwork-interface'

import { CONST } from '../const/const'
import { signedStatuses } from '../const/data/v01/rider-team-members/firestore-v01-rider-team-member-const'

export const getRiderHorses = (horses: IHorseData[] | null, horseIds: IHorseSelected[]) => {
  if (horses?.[0] && horseIds?.[0]) {
    return horses.filter((item) => horseIds?.find((horse) => horse.id === item.id))
  } else {
    return []
  }
}

export const getRiderTeamMembersByRole = (
  eventTeamMembers: IEventTeamMemberInterface,
  rider: IRegistrationByDayInterface,
  role: string
) => {
  const teamMembersNames: Set<string> = new Set()
  const teamMembers: IRiderTeamMemberInterface[] = []

  eventTeamMembers?.[role]?.forEach((current) => {
    if (
      current.rider?.riderId === rider.riderId &&
      current.rider?.horsesSelected?.[0] &&
      current.rider?.horsesSelected?.find((item) =>
        rider.horsesSelected?.find((horse) => horse.id === item.id)
      )
    ) {
      if (current.teamMember.teamMemberName) teamMembersNames.add(current.teamMember.teamMemberName)
      teamMembers.push(current.teamMember)
    }
  })
  return { teamMembersNames, teamMembers }
}

export const getAdminEntries = ({
  registrationsByDay,
  fees,
  eventRegisteredUsers,
  riderTeamMembers,
  usersDocuments,
  horseCompetitionPaperwork,
  horses,
  eventReviewPublish,
  users,
}: {
  registrationsByDay: IRegistrationByDayInterface[]
  fees: IRegistrationFeesInterface[]
  eventRegisteredUsers: TEventRegisteredUsers[]
  riderTeamMembers: IRiderTeamMemberInterface[]
  usersDocuments: IUserDocument[]
  eventReviewPublish: IEventReviewPublish | null
  horseCompetitionPaperwork: IHorseCompetitorDocument[]
  horses: IHorseData[]
  users: IUserInterface[]
}) => {
  const adminEntries: IRider[] = []

  const newRegisteredUsersByDay = registrationsByDay.map((registeredUserByDay) => ({
    ...registeredUserByDay,
    riderTeamMember: riderTeamMembers.find(
      (rtm) => rtm.registrationDocId === registeredUserByDay.registrationDocId
    ),
  })) as IRegisteredUser[]

  newRegisteredUsersByDay.forEach((rider, index) => {
    const registeredUser = eventRegisteredUsers.find((user) => rider.userId === user.userId)
    if (registeredUser) {
      newRegisteredUsersByDay[index] = {
        ...rider,
        answers: registeredUser.answers,
      }
    }
  })

  const groupedByHorseAndRider = newRegisteredUsersByDay.reduce((acc: any, item) => {
    const horseIds = item.horsesSelected.map(({ id }) => id)?.join('')
    const key = `${horseIds}_${item.riderId}`

    if (!acc[key]) {
      acc[key] = { ...item, classes: [], scratchedClasses: [] }
    }
    if (item.registrationByDayName && !item.isScratched) {
      acc[key].classes.push(item)
    } else if (item.registrationByDayName && item.isScratched) {
      acc[key].scratchedClasses.push(item.registrationByDayName)
    }

    return acc
  }, {})

  newRegisteredUsersByDay.forEach((rider) => {
    const riderUser = users.find((user) => user.id === rider.riderId) || null

    const horseIds = rider.horsesSelected.map(({ id }) => id)?.join('')

    let classes: IRegistrationByDayInterface[] = []
    let scratchedClasses: string[] = []

    if (groupedByHorseAndRider[`${horseIds}_${rider.riderId}`]) {
      classes = [...groupedByHorseAndRider[`${horseIds}_${rider.riderId}`].classes]
      scratchedClasses = [
        ...groupedByHorseAndRider[`${horseIds}_${rider.riderId}`].scratchedClasses,
      ]
    }
    const isAllScratched = classes.length === 0

    const hasRider = adminEntries.find(
      (rd) =>
        rd.registrationDocId === rider.registrationDocId &&
        rd.riderId === rider.riderId &&
        isEqual(rd.horsesSelected, rider.horsesSelected)
    )

    if (!hasRider && riderUser) {
      // calculate required fields issues
      let requiredIssues = 0
      const requiredFields = getUserRequiredFields({
        user: riderUser,
        eventRequiredUserFields: eventReviewPublish?.EventRequiredFields?.requiredFields,
      })

      requiredFields?.forEach((field) => {
        if (hasInvalidRequiredField(field)) {
          requiredIssues += 1
        }
      })

      // calculate classes issues
      const classesIssues = classes.filter(
        (item) =>
          isPaymentPending(item.isScratched, item.paymentStatus) ||
          isRefundPending(
            item.isScratched,
            item.refundAmountAdded,
            item.paymentStatus,
            item.paymentType
          )
      )

      // calculate fees issues
      const feesIssues = fees.filter(
        (item) =>
          item.ownerId === rider.riderId &&
          item.registrationDocId === rider.registrationDocId &&
          (isPaymentPending(item.isScratched, item.paymentStatus) ||
            isRefundPending(
              item.isScratched,
              item.refundAmountAdded,
              item.paymentStatus,
              item.paymentType
            ))
      )

      // calculate paperwork issues
      let paperworkIssues = 0

      // unique team members for rider
      const riderTeamMembersFiltered = riderTeamMembers.filter(
        (item) =>
          item.riderId === rider.riderId &&
          rider.horsesSelected.some((horse) => horse.id === item.horseId) &&
          item.registrationDocId === rider.registrationDocId
      )

      riderTeamMembersFiltered.forEach((riderTeamMember) => {
        const currentUsersDocuments = usersDocuments.filter(
          (item) => item.riderTeamMemberDocId === riderTeamMember.id
        )

        currentUsersDocuments?.forEach((usersDocument) => {
          if (usersDocument.riderTeamMemberDocId === riderTeamMember.id) {
            const guardian =
              riderTeamMember.teamMemberRole === CONST.UI.TEAM_MEMBERS_ROLES.GUARDIAN &&
              riderTeamMember.riderId === usersDocument.riderId

            const missedGuardian =
              calculateAge(riderTeamMember?.teamMemberDob) < 18 &&
              riderTeamMember.teamMemberRole === CONST.UI.TEAM_MEMBERS_ROLES.RIDER &&
              !guardian

            if (usersDocument.status === signedStatuses.NOT_SIGNED || missedGuardian) {
              paperworkIssues++
            }
          }
        })
      })

      const haveRider = haveTeamMember(riderTeamMembersFiltered, CONST.UI.TEAM_MEMBERS_ROLES.RIDER)
      if (!haveRider) paperworkIssues += 1

      // calculate team issues
      const teamIssues = riderTeamMembersFiltered
        .filter((item) => item.teamMemberRole !== CONST.UI.TEAM_MEMBERS_ROLES.RIDER)
        .filter((riderTeamMember) => {
          const fields = getUserRequiredFields({
            user: users.find((user) => user.id === riderTeamMember.teamMemberId) || null,
            eventRequiredUserFields: eventReviewPublish?.EventRequiredFields.requiredFields || [],
          })

          const fieldsFiltered = fields.filter((field) => !hasInvalidRequiredField(field))

          return fields.length - fieldsFiltered.length > 0
        })

      // calculate horses issues
      let horsesIssues = 0

      // unique horse competition paperwork  for rider
      const horseCompetitionPaperworkFiltered = horseCompetitionPaperwork.filter((item) => {
        const horse = horses.find((horse) => horse.horseId === item.horseId)
        return rider.horsesSelected.some((selected) => selected.id === horse?.id)
      })

      horseCompetitionPaperworkFiltered.forEach((horseWithPaperwork) => {
        const fields = getHorseRequiredFields({
          horse: horses.find((horse) => horse.horseId === horseWithPaperwork.horseId) || null,
          paperwork: horseWithPaperwork,
          eventRequiredHorseFields:
            eventReviewPublish?.EventRequiredFields.requiredHorseFields || [],
        })

        const fieldsFiltered = fields.filter((field) => !hasInvalidRequiredField(field))
        horsesIssues += fields.length - fieldsFiltered.length || 0
      })

      adminEntries.push({
        ...rider,
        isScratched: isAllScratched,
        documents: [],
        classes,
        scratchedClasses,
        requiredIssues: requiredIssues,
        showRequiredIssues: true,
        classesIssues: classesIssues.length,
        showClassesIssues: true,
        feesIssues: feesIssues.length,
        showFeesIssues: true,
        paperworkIssues: paperworkIssues,
        showPaperworkIssues: true,
        teamIssues: teamIssues.length,
        showTeamIssues: true,
        horsesIssues: horsesIssues,
        showHorsesIssues: true,
      })
    }
  })

  return { adminEntries }
}
